import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth0, User as Auth0User } from '@auth0/auth0-react';
import { User } from '../types';
import { userAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: () => Promise<void>;
  logout: () => void;
  updateUser: (userData: User) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to map Auth0 user to our User type
const mapAuth0UserToUser = (auth0User: Auth0User | undefined, token: string | null): User | null => {
  if (!auth0User) return null;

  // Extract roles from token or user metadata
  const roles = (auth0User as any)['https://mechanisticminds.com/roles'] || 
                auth0User['https://api.mechanisticminds.com/roles'] ||
                auth0User.app_metadata?.role || 
                'user';

  return {
    id: parseInt(auth0User.sub?.split('|')[1] || '0') || 0, // Extract numeric ID if available
    email: auth0User.email || '',
    firstName: auth0User.given_name || auth0User.name?.split(' ')[0] || '',
    lastName: auth0User.family_name || auth0User.name?.split(' ').slice(1).join(' ') || '',
    role: Array.isArray(roles) ? roles[0] : roles,
    institution: auth0User.user_metadata?.institution,
    department: auth0User.user_metadata?.department,
    phone: auth0User.user_metadata?.phone,
    bio: auth0User.user_metadata?.bio,
    isActive: auth0User.app_metadata?.is_active !== false,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error: auth0Error
  } = useAuth0();

  const [accessToken, setAccessToken] = React.useState<string | null>(null);
  const [isTokenLoading, setIsTokenLoading] = React.useState(true);
  const [dbUser, setDbUser] = React.useState<User | null>(null);
  const [isUserLoading, setIsUserLoading] = React.useState(false);

  // Get access token when user is authenticated
  React.useEffect(() => {
    if (auth0IsAuthenticated && auth0User) {
      getAccessTokenSilently()
        .then(token => {
          // Token is a string, not an object
          const tokenString = typeof token === 'string' ? token : (token as any).access_token || '';
          setAccessToken(tokenString);
          // Store token in localStorage for API interceptor
          localStorage.setItem('token', tokenString);
          setIsTokenLoading(false);
        })
        .catch(err => {
          console.error('Error getting access token:', err);
          localStorage.removeItem('token');
          setAccessToken(null);
          setIsTokenLoading(false);
        });
    } else {
      localStorage.removeItem('token');
      setAccessToken(null);
      setIsTokenLoading(false);
      setDbUser(null);
    }
  }, [auth0IsAuthenticated, auth0User, getAccessTokenSilently]);

  // Fetch user data from database after authentication and token is ready
  React.useEffect(() => {
    if (auth0IsAuthenticated && accessToken && !isTokenLoading) {
      setIsUserLoading(true);
      userAPI.getCurrentUser()
        .then(user => {
          setDbUser(user);
          setIsUserLoading(false);
        })
        .catch(err => {
          console.error('Error fetching user from database:', err);
          // Fallback to Auth0 user data if database fetch fails
          setIsUserLoading(false);
        });
    }
  }, [auth0IsAuthenticated, accessToken, isTokenLoading]);

  // Log Auth0 errors
  React.useEffect(() => {
    if (auth0Error) {
      console.error('Auth0 error:', auth0Error);
    }
  }, [auth0Error]);

  const login = async () => {
    try {
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname
        }
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear local storage first
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAccessToken(null);
    
    // Logout from Auth0
    try {
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin + '/login',
          federated: false // Don't log out from identity provider
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
      // If Auth0 logout fails, still redirect to login page
      window.location.href = '/login';
    }
  };

  const updateUser = (userData: User) => {
    // Update local state when user profile is updated
    setDbUser(userData);
  };

  const getToken = async (): Promise<string> => {
    try {
      const token = await getAccessTokenSilently();
      // Token is a string, not an object
      const tokenString = typeof token === 'string' ? token : (token as any).access_token || '';
      setAccessToken(tokenString);
      localStorage.setItem('token', tokenString);
      return tokenString;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  // Use database user data if available, otherwise fallback to Auth0 user data
  const user = dbUser || mapAuth0UserToUser(auth0User, accessToken);
  const isLoading = auth0IsLoading || isTokenLoading || isUserLoading;
  const isAuthenticated = auth0IsAuthenticated && !!accessToken && !!user;

  const value: AuthContextType = {
    user,
    token: accessToken,
    login,
    logout,
    updateUser,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently: getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
